// ---------- settings dialog ----------
// Opened from the catalogue's third tab. A dialog and not a fourth shelf: four switches and three
// rows that lead elsewhere, and a screen of its own would make them feel like a destination when
// they are a two-second detour. Closing it returns to exactly the tab that was being read.
//
// The three rows below the switches are not preferences and have no stored value: the ad consent
// (Google's form owns it), the privacy policy (a page), and the deletion of everything (an act).
// They are here because this is where a player looks for them — and, for the last one, because the
// app is the ONLY place it can live at all: see wipe().
//
// The switches display `settings()` and never their own copy of the truth: any change anywhere
// repaints them through wireSettings, so the panel's Son button and this dialog can never
// disagree.

import {
  adoptUser,
  currentUsername,
  currentUser,
  ensureUser,
  forgetUser,
  refreshUsername,
  setUsername,
} from './account';
import { deleteAccount, fetchRecoveryCode, redeemRecoveryCode, updateUsername } from './api';
import { hideBannerForInput, resumeBannerAfterInput } from './ads';
import { coachShowGesturesAgain } from './coach';
import { consentGate, openPrivacyOptions } from './consent';
import { currentLang, inkLabel, LANG_NAMES, LANGS, setLang, t, taglineIn } from './i18n';
import { buzz, isNative } from './platform';
import { clearAllProgress } from './progress';
import { clearCreations } from './creationStore';
import { resyncAll } from './sync';
import { randomUsername } from './pseudo';
import { noMotion } from './anim';
import { disablePush, enablePush } from './push';
import { INKS } from '../shared/ink';
import { MARK_HUES, MARK_STYLES } from './mark';
import { SKINS } from './skin';
import { setSetting, settings, wireSettings, type Settings } from './settings';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;

/** Each row of the dialog, and the preference it drives. `flood` and `zen` sont délibérément
 *  absents : tous deux sont la règle du jeu et non un choix, et chacun le dit à sa déclaration
 *  dans Settings. */
const ROWS: Array<[string, keyof Settings]> = [
  ['prefSound', 'sound'],
  ['prefHaptics', 'haptics'],
  ['prefMotion', 'motion'],
  ['prefCompass', 'compass'],
];

/** Même forme que `validUsername` dans `backend/src/validation.ts` — dupliquée ici parce que le
 *  backend n'importe jamais `shared/`, donc rien de commun où poser une seule définition. Un
 *  contrôle immédiat, sans aller-retour réseau pour une faute de frappe ; celui qui compte est
 *  côté serveur (§ saveUsername). */
const USERNAME_RE = /^[a-zA-Z][a-zA-Z0-9_]{2,19}$/;
/** Le format que le serveur pose par défaut (`guestN`) — réservé, jamais choisissable à la main.
 *  Même jumeau que `RESERVED_GUEST_NAME` côté backend, mêmes raisons. */
const RESERVED_GUEST_NAME = /^guest[0-9]+$/i;

function validUsername(v: string): boolean {
  return USERNAME_RE.test(v) && !RESERVED_GUEST_NAME.test(v);
}

/**
 * Les pastilles d'encre. Construites une fois, repeintes à chaque ouverture : la sélection est
 * lue dans `settings()` comme celle des interrupteurs, jamais gardée ici.
 */
function buildInks(): void {
  const row = $('prefInks');
  if (row.childElementCount) return;
  for (const ink of INKS) {
    const b = document.createElement('button');
    b.className = 'ink-dot';
    b.dataset.ink = ink.id;
    const label = inkLabel(ink.id);
    b.title = label;
    b.setAttribute('aria-label', label);
    b.style.setProperty('--dot', ink.css);
    b.onclick = () => {
      setSetting('ink', ink.id);
      buzz('light');
    };
    row.appendChild(b);
  }
}

/**
 * Les pastilles de thème. Même idiome que les encres — on choisit en voyant, pas en lisant —
 * mais la pastille est ici un écran miniature : le sol du thème, et sa couleur d'accent posée
 * dessus. Les deux valeurs viennent de src/skin.ts et sont écrites en style inline, parce
 * qu'une pastille doit montrer le thème QU'ELLE PROPOSE : la charte, elle, ne connaît que le
 * thème actif.
 */
/**
 * La feuille de choix de la langue. Construite ici plutôt qu'écrite dans index.html : la liste est
 * celle des dictionnaires (LANGS), donc ajouter une langue ne demande plus de penser à un balisage
 * qui vit dans un autre fichier — même raisonnement que les pastilles d'encre et de thème.
 *
 * Chaque ligne montre le nom de la langue DANS elle-même, puis l'accroche du jeu traduite. C'est
 * ce qui remplace les drapeaux : un drapeau nomme un pays, pas une langue, et il se trompe
 * exactement là où sont les joueurs — le portugais du Brésil sous un drapeau portugais, l'espagnol
 * du Mexique sous un drapeau espagnol. Une phrase, elle, ne se trompe de personne.
 *
 * Chaque bouton porte son `lang=` : sans lui, un lecteur d'écran prononce « Português » avec la
 * phonétique de la langue active, et le navigateur y coupe les mots selon les mauvaises règles.
 */
function buildLangSheet(): void {
  const list = $('langSheetBody');
  if (list.childElementCount) return;
  for (const l of LANGS) {
    const b = document.createElement('button');
    b.className = 'lang-opt';
    b.dataset.lang = l;
    b.lang = l;
    const n = document.createElement('span');
    n.className = 'n';
    n.textContent = LANG_NAMES[l];
    const sub = document.createElement('span');
    sub.className = 's';
    sub.textContent = taglineIn(l);
    b.append(n, sub);
    b.onclick = () => {
      // Choisir la langue déjà active n'est pas un changement : setLang() le sait et ne fait
      // rien, mais un rechargement raté est une seconde d'écran blanc pour rien — on referme.
      if (l === currentLang()) {
        closeLangSheet();
        buzz('light');
        return;
      }
      // Toutes désactivées au clic plutôt qu'après : setLang() recharge la page dans la foulée, il
      // n'y a pas de retour à peindre — même logique que wipe()/saveUsername() désactivant leur
      // contrôle pendant une action qui va de toute façon changer d'écran.
      for (const el of list.children) (el as HTMLButtonElement).disabled = true;
      setLang(l);
    };
    list.appendChild(b);
  }
}

function openLangSheet(): void {
  buildLangSheet();
  for (const b of $('langSheetBody').children)
    b.classList.toggle('on', (b as HTMLElement).dataset['lang'] === currentLang());
  $('langSheet').setAttribute('aria-hidden', 'false');
}

/** Même contrat que closeUsernameDialog() — ce que la chaîne du bouton retour attend : vrai
 *  seulement si le geste a bien refermé quelque chose. */
export function closeLangSheet(): boolean {
  if ($('langSheet').getAttribute('aria-hidden') !== 'false') return false;
  $('langSheet').setAttribute('aria-hidden', 'true');
  return true;
}

function buildSkins(): void {
  const row = $('prefSkins');
  if (row.childElementCount) return;
  for (const k of SKINS) {
    const b = document.createElement('button');
    b.className = 'ink-dot skin-dot';
    b.dataset.skin = k.id;
    b.title = k.label;
    b.setAttribute('aria-label', k.label);
    b.style.setProperty('--ground', k.ground);
    b.style.setProperty('--dot', k.accent);
    b.onclick = () => {
      setSetting('skin', k.id);
      buzz('light');
    };
    row.appendChild(b);
  }
}

/**
 * Les pastilles du repère : sa forme, puis sa teinte. Construites une fois comme les encres, mais
 * REPEINTES à chaque ouverture — les trois formes s'affichent dans la teinte choisie, donc changer
 * de teinte les change toutes les trois. Un aperçu qui mentirait sur la couleur ne servirait à
 * rien : ces pastilles existent pour qu'on choisisse en voyant.
 */
function buildMarks(): void {
  const forme = $('prefMarks');
  if (!forme.childElementCount) {
    for (const st of MARK_STYLES) {
      const b = document.createElement('button');
      b.className = `ink-dot mark-dot st-${st.id}`;
      b.dataset.mark = st.id;
      b.title = t('mark.tooltip', { label: st.label, hint: st.hint });
      b.setAttribute('aria-label', t('mark.ariaLabel', { label: st.label, hint: st.hint }));
      b.onclick = () => {
        setSetting('markStyle', st.id);
        buzz('light');
      };
      forme.appendChild(b);
    }
  }
  const teinte = $('prefMarkHues');
  if (teinte.childElementCount) return;
  for (const h of MARK_HUES) {
    const b = document.createElement('button');
    b.className = 'ink-dot';
    b.dataset.hue = h.id;
    b.title = h.label;
    b.setAttribute('aria-label', h.label);
    b.style.setProperty('--dot', h.css);
    b.onclick = () => {
      setSetting('markHue', h.id);
      buzz('light');
    };
    teinte.appendChild(b);
  }
}

function paint(): void {
  const s = settings();
  $('prefLangValue').textContent = LANG_NAMES[currentLang()];
  $('prefLangValue').lang = currentLang();
  for (const [id, key] of ROWS) $(id).setAttribute('aria-checked', String(s[key]));
  // Pas dans ROWS (activer demande un aller-retour asynchrone, cf. wirePrefs ci-dessous), donc
  // peint à part. Masqué sur le web : pas de canal push hors de l'APK (src/push.ts).
  $('prefPush').hidden = !isNative();
  $('prefPush').setAttribute('aria-checked', String(s.pushEnabled));
  buildSkins();
  for (const b of $('prefSkins').children)
    b.classList.toggle('on', (b as HTMLElement).dataset['skin'] === s.skin);
  buildInks();
  for (const b of $('prefInks').children)
    b.classList.toggle('on', (b as HTMLElement).dataset['ink'] === s.ink);
  buildMarks();
  const teinte = MARK_HUES.find((h) => h.id === s.markHue) ?? MARK_HUES[0];
  for (const b of $('prefMarks').children) {
    const el = b as HTMLElement;
    el.style.setProperty('--dot', teinte.css);
    el.classList.toggle('on', el.dataset['mark'] === s.markStyle);
  }
  for (const b of $('prefMarkHues').children)
    b.classList.toggle('on', (b as HTMLElement).dataset['hue'] === s.markHue);
  // Not a preference of ours, so it is not in ROWS and has no stored value: whether the row exists
  // at all is Google's answer, read from the last consent gathering. Painted on every open rather
  // than once at startup, because the answer changes — the form itself can retire the requirement,
  // and on the very first launch the row appears while the dialog may already be built.
  $('prefAds').hidden = !consentGate().privacyOptions;
  // Remet la ligne de suppression dans son état neutre : elle porte peut-être encore le message
  // d'échec de la tentative précédente, et un panneau réouvert repart d'une page propre.
  const wipe = $<HTMLButtonElement>('prefWipe');
  wipe.disabled = false;
  wipe.querySelector('em')!.textContent = t('prefs.wipe.hint');
}

/**
 * Ouvre #usernameDialog — depuis Réglages, et depuis le crayon de l'écran « Moi » (src/profile.ts),
 * qui suit le nouveau nom par onUsernameChange() (account.ts) —, préremplie sans jamais créer de
 * compte pour autant : rien n'est en cache
 * tant que « Valider » n'a jamais été pressé (paresse voulue, cf. src/account.ts). Un compte qui
 * existe déjà mais dont ce cache n'a pas le pseudo (un appareil antérieur à cette fonctionnalité,
 * un cache partiellement vidé) va le rechercher sur le serveur.
 *
 * Une feuille à part plutôt qu'une rangée inline dans Réglages (cf. la note sur
 * #prefUsernameOpen dans index.html) : sur un vrai téléphone, ce champ posé au milieu d'une
 * longue liste défilante entrait en conflit avec le clavier virtuel — un menu natif se posait
 * par-dessus la rangée suivante. Isolée, sans liste défilante autour d'elle, le problème
 * disparaît.
 */
export async function openUsernameDialog(): Promise<void> {
  const input = $<HTMLInputElement>('prefUsername');
  const btn = $<HTMLButtonElement>('prefUsernameSave');
  btn.disabled = false;
  btn.textContent = t('prefs.username.save');
  $('prefUsernameErr').hidden = true;
  $('usernameDialog').setAttribute('aria-hidden', 'false');
  let name = await currentUsername();
  if (!name && (await currentUser())) name = await refreshUsername();
  input.value = name ?? '';
  updateUsernameSaveState();
}

/** Renvoie true quand elle a effectivement refermé quelque chose, ce qu'attend le bouton
 *  « précédent » d'Android pour savoir s'il a consommé le geste — cf. closeOverlays() dans
 *  ui.ts. Vérifiée AVANT le retour de l'onglet Réglages dans cette chaîne : cette feuille
 *  s'ouvre par-dessus la page des réglages, donc c'est elle la plus dessus. */
export function closeUsernameDialog(): boolean {
  if ($('usernameDialog').getAttribute('aria-hidden') !== 'false') return false;
  $('usernameDialog').setAttribute('aria-hidden', 'true');
  return true;
}

function updateUsernameSaveState(): void {
  $<HTMLButtonElement>('prefUsernameSave').disabled = !validUsername(
    $<HTMLInputElement>('prefUsername').value,
  );
}

/**
 * Valide le nouveau pseudo. Même chorégraphie que wipe() : désactiver, un libellé qui dit ce qui
 * se passe, réactiver avec soit le nouveau pseudo en cache, soit l'erreur affichée en ligne.
 * `ensureUser()` est appelé ICI et pas avant — ouvrir Réglages ne doit créer aucun compte.
 */
async function saveUsername(): Promise<void> {
  const input = $<HTMLInputElement>('prefUsername');
  const btn = $<HTMLButtonElement>('prefUsernameSave');
  const err = $<HTMLParagraphElement>('prefUsernameErr');
  const name = input.value;
  if (!validUsername(name)) return;

  btn.disabled = true;
  input.disabled = true;
  err.hidden = true;
  btn.textContent = t('prefs.username.saving');
  try {
    const token = await ensureUser();
    if (!token) throw new Error('offline');
    const result = await updateUsername(token, name);
    if (result.ok) {
      await setUsername(result.username);
      input.value = result.username;
      btn.textContent = t('prefs.username.save');
      closeUsernameDialog();
    } else {
      err.textContent =
        result.reason === 'taken' ? t('prefs.username.taken') : t('prefs.username.rejected');
      err.hidden = false;
      btn.textContent = t('prefs.username.save');
    }
  } catch {
    err.textContent = t('prefs.username.failed');
    err.hidden = false;
    btn.textContent = t('prefs.username.save');
  } finally {
    input.disabled = false;
    updateUsernameSaveState();
  }
}

// ---------- le code de reprise ----------
// Quatre mots attachés au compte, à noter d'un côté et à retaper de l'autre. C'est ce qui manquait
// pour qu'une progression survive à une DÉSINSTALLATION ou à un changement de téléphone : le jeton
// du compte vit dans le stockage de l'application, il part avec elle, et rien ne permettait de le
// faire revenir (cf. src/account.ts, backend/src/utils/recovery.ts).
//
// Un seul dialogue pour les deux sens, et non deux rangées : « voici ton code » et « j'ai un code »
// sont les deux moitiés d'une même question, et celui qui vient de changer de téléphone cherche la
// seconde à l'endroit où il a lu la première.

/**
 * Recharger le catalogue après une reprise, injecté par main.ts.
 *
 * Une injection et non un import de home.ts : home.ts importe DÉJÀ ce module (refreshPrefs), donc
 * l'appeler directement fermerait un cycle — même raison et même forme que wireCodeProgress dans
 * render.ts. Et ce rechargement n'est pas cosmétique : la liste elle-même dépend du compte, un
 * pack débloqué ajoutant ses puzzles au catalogue.
 */
let reloadCatalogue: () => Promise<unknown> = async () => {};

/** Même forme que `validRecoveryCode` et `normalizeRecoveryCode` côté serveur
 *  (backend/src/utils/recovery.ts, backend/src/validation.ts) — dupliquée ici pour la même raison
 *  que `validUsername` juste au-dessus : le frontend n'importe pas le backend. Elle ne sert qu'à
 *  décider si le bouton s'allume ; c'est le serveur qui tranche, et lui seul connaît les mots.
 *
 *  QUATRE OU CINQ mots : les codes neufs en font cinq, ceux distribués avant le 2026-09-05 en
 *  font quatre et doivent continuer d'ouvrir le bouton — refuser ici rendrait une progression
 *  irrécupérable sans même appeler le serveur. */
function normalizeCode(v: string): string {
  return v
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function validCode(v: string): boolean {
  return /^[a-z]{2,12}(-[a-z]{2,12}){3,4}$/.test(normalizeCode(v));
}

/**
 * Ouvre #recoveryDialog et va chercher le code de ce compte.
 *
 * `ensureUser()` est appelé ici, et c'est le seul endroit de ce fichier où créer un compte se
 * justifie AVANT que le joueur n'ait validé quoi que ce soit : demander son code de reprise EST
 * demander une sauvegarde, et il n'y a rien à quoi attacher un code sans compte. Ouvrir Réglages,
 * lui, ne crée toujours rien.
 *
 * Le garde-fou du `aria-hidden` avant d'écrire : la requête peut revenir après que le joueur a
 * refermé, et repeindre un dialogue fermé y laisserait le code de la fois précédente à la
 * prochaine ouverture.
 */
async function openRecoveryDialog(): Promise<void> {
  const code = $('prefRecoveryCode');
  const input = $<HTMLInputElement>('prefRecoveryInput');
  const btn = $<HTMLButtonElement>('prefRecoveryUse');
  const err = $<HTMLParagraphElement>('prefRecoveryErr');
  input.value = '';
  input.disabled = false;
  btn.disabled = true;
  btn.textContent = t('prefs.recovery.use');
  $<HTMLButtonElement>('prefRecoveryCopy').textContent = t('prefs.recovery.copy');
  err.hidden = true;
  err.classList.remove('ok');
  code.textContent = t('prefs.recovery.loading');
  $('recoveryDialog').setAttribute('aria-hidden', 'false');

  let phrase: string | null = null;
  try {
    const token = await ensureUser();
    if (token) phrase = await fetchRecoveryCode(token);
  } catch {
    /* hors connexion, ou un jeton que le serveur ne connaît plus : la synchro du prochain
       lancement rattrape le second cas (cf. recover() dans sync.ts), et il n'y a rien à dire de
       plus ici que « pas maintenant ». */
  }
  if ($('recoveryDialog').getAttribute('aria-hidden') !== 'false') return;
  code.textContent = phrase ?? t('prefs.recovery.offline');
}

/** Même contrat que closeUsernameDialog() : vrai si quelque chose a été refermé, pour la chaîne
 *  du bouton « précédent » (cf. closeOverlays() dans ui.ts). */
export function closeRecoveryDialog(): boolean {
  if ($('recoveryDialog').getAttribute('aria-hidden') !== 'false') return false;
  $('recoveryDialog').setAttribute('aria-hidden', 'true');
  return true;
}

/** Le presse-papier peut manquer (contexte non sécurisé, permission refusée) et ce n'est pas une
 *  panne : le code est du texte sélectionnable, il reste à l'écran. On ne dit donc rien de plus
 *  que « copié » quand ça a marché. */
async function copyRecoveryCode(): Promise<void> {
  const phrase = $('prefRecoveryCode').textContent ?? '';
  if (!phrase || !validCode(phrase)) return;
  try {
    await navigator.clipboard.writeText(phrase);
    $<HTMLButtonElement>('prefRecoveryCopy').textContent = t('prefs.recovery.copied');
    buzz('light');
  } catch {
    /* rien : le code est lisible et sélectionnable */
  }
}

/**
 * Échange le code tapé contre le compte qu'il désigne, et cet appareil devient celui de ce compte.
 *
 * L'ordre compte : adopter le jeton, relire l'identité du compte retrouvé (le pseudo arrive avec
 * la réponse, le drapeau de la ligue non), puis resynchroniser — c'est `resyncAll()` qui tire la
 * progression et pousse ce que cet appareil avait, dont le serveur fait l'union. Rien n'est effacé
 * nulle part : personne ne perd ce qu'il vient de peindre en retapant un code.
 *
 * Le dialogue reste OUVERT sur son succès, avec une ligne qui le dit. Se refermer tout seul est
 * ambigu — c'est aussi ce que fait « Annuler » — et cet écran-là est le seul accusé de réception
 * d'une opération dont le résultat vit ailleurs, dans un catalogue que le joueur n'a pas sous les
 * yeux.
 */
async function useRecoveryCode(): Promise<void> {
  const input = $<HTMLInputElement>('prefRecoveryInput');
  const btn = $<HTMLButtonElement>('prefRecoveryUse');
  const err = $<HTMLParagraphElement>('prefRecoveryErr');
  const typed = input.value;
  if (!validCode(typed)) return;

  btn.disabled = true;
  input.disabled = true;
  err.hidden = true;
  err.classList.remove('ok');
  btn.textContent = t('prefs.recovery.restoring');
  try {
    const found = await redeemRecoveryCode(normalizeCode(typed));
    if (!found) {
      err.textContent = t('prefs.recovery.unknown');
      err.hidden = false;
      return;
    }
    await adoptUser(found.id, found.username);
    await refreshUsername();
    await resyncAll();
    await reloadCatalogue();
    err.textContent = t('prefs.recovery.done');
    err.classList.add('ok');
    err.hidden = false;
    input.value = '';
    // Le code affiché en haut est celui de l'ANCIEN compte : celui du compte retrouvé le remplace,
    // c'est le seul qui vaille encore quelque chose à noter.
    $('prefRecoveryCode').textContent = normalizeCode(typed);
    buzz('medium');
  } catch {
    err.textContent = t('prefs.recovery.failed');
    err.hidden = false;
  } finally {
    btn.textContent = t('prefs.recovery.use');
    input.disabled = false;
    btn.disabled = !validCode(input.value);
  }
}

/** Repeint les rangées depuis `settings()`. Appelée par home.ts quand l'onglet Réglages
 *  s'allume, là où c'était `openPrefs()` qui le faisait à l'ouverture du dialogue : Réglages est
 *  une PAGE maintenant, elle n'a plus rien à ouvrir ni à fermer. Le reste du temps, c'est
 *  `wireSettings(paint)` en bas de ce fichier qui la rappelle à chaque changement. */
export function refreshPrefs(): void {
  paint();
}

/**
 * « Supprimer mes données » — le compte anonyme et toutes les sauvegardes, ici et sur le serveur.
 *
 * L'ORDRE EST LE TOUT DE CETTE FONCTION : le serveur d'abord, l'appareil ensuite, et rien
 * localement si le serveur a refusé. Le compte ne contient ni nom ni e-mail : le jeton détenu par
 * cet appareil est la seule chose au monde qui désigne la ligne. Effacer l'appareil sur un échec
 * réseau laisserait des données que plus personne ne peut demander à supprimer — l'inverse exact
 * de ce que le joueur vient de demander.
 *
 * Un rechargement pour finir, plutôt qu'un rafraîchissement d'écrans : la partie en cours tient
 * encore ses taps en mémoire et le prochain enregistrement les réécrirait. Recharger est la seule
 * façon simple de garantir qu'il ne reste rien à ressusciter.
 */
async function wipe(): Promise<void> {
  const btn = $<HTMLButtonElement>('prefWipe');
  const label = btn.querySelector('em')!;
  if (!confirm(t('prefs.wipe.confirm'))) return;
  btn.disabled = true;
  label.textContent = t('prefs.wipe.progress');
  try {
    // Les créations D'ABORD : elles ne vivent plus que sur ce téléphone (src/creationStore.ts) et
    // viennent des photos du joueur. Un échec ici (stockage indisponible) doit se dire comme celui
    // du serveur, et avant d'avoir rien touché d'autre : un « c'est fait » suivi d'un rechargement
    // les laissait en place sans un mot (revue du 2026-10-30).
    await clearCreations();
    const token = await currentUser();
    // Pas de jeton : rien n'est jamais parti dans le cloud, il n'y a que du local à effacer.
    if (token) await deleteAccount(token);
    clearAllProgress();
    // Ce qui décrit le compte effacé ne doit pas survivre sur l'appareil : badges en cache (l'écran
    // « Moi » les réaffichait), relevé des paliers déjà célébrés, partages en attente, chiffres de
    // la semaine. Écrit ici en clair plutôt qu'importé des modules qui les possèdent : profile.ts
    // importe déjà ce fichier, et le rechargement qui suit vide de toute façon la mémoire.
    for (const key of [
      'codigrid:badges:v1',
      'codigrid:badges:seen:v1',
      'codigrid:shares:v1',
      'codigrid:community:v1',
      'codigrid.creationsDeleted', // cf. creationsDeleted dans home.ts : le compte n'est plus
    ]) {
      try {
        localStorage.removeItem(key);
      } catch {
        /* stockage indisponible : il n'y a alors rien d'écrit non plus */
      }
    }
    await forgetUser();
    location.reload();
  } catch {
    btn.disabled = false;
    // Le libellé d'origine revient à la prochaine ouverture du panneau (paint()), pas avant : le
    // joueur doit avoir le temps de lire pourquoi ses données sont toujours là.
    label.textContent = t('prefs.wipe.failed');
  }
}

export function wirePrefs(reload: () => Promise<unknown>): void {
  // Cf. reloadCatalogue plus haut : passé par main.ts pour ne pas fermer de cycle d'imports.
  reloadCatalogue = reload;
  // La rangée de la langue ouvre sa feuille ; les lignes DEDANS naissent câblées dans
  // buildLangSheet(), comme les pastilles d'encre et de thème.
  $('prefLangOpen').onclick = () => openLangSheet();
  $('langSheetClose').onclick = () => closeLangSheet();
  // Le fond referme, comme pour la feuille des facettes : un choix qu'on n'a pas fait se quitte
  // en tapant à côté.
  $('langSheet').onclick = (e) => {
    if (e.target === $('langSheet')) closeLangSheet();
  };
  // Le dialogue des deux gestes s'ouvre sur-le-champ, par-dessus les réglages (cf. la note de
  // coachShowGesturesAgain). Plus de buzz : il servait d'accusé de réception à une rangée dont
  // l'effet ne se voyait qu'à la feuille suivante, et l'effet se voit maintenant.
  $('prefCoach').onclick = () => coachShowGesturesAgain();
  for (const [id, key] of ROWS) {
    $(id).onclick = () => {
      const next = !settings()[key];
      setSetting(key, next);
      // Feel the switch you just flipped. Turning haptics OFF buzzes nothing, which is itself
      // the right answer — buzz() is already silent by then.
      buzz('light');
    };
  }
  // Asynchrone et donc à part de ROWS : allumer demande une permission système (le joueur peut la
  // refuser) et un aller-retour réseau (le token doit atteindre le serveur). L'interrupteur ne
  // bouge qu'une fois ce résultat connu, jamais en optimiste — sinon il mentirait sur ce qui va
  // réellement arriver dans la barre de notifications.
  $('prefPush').onclick = () => {
    buzz('light');
    const next = !settings().pushEnabled;
    void (next ? enablePush() : disablePush().then(() => true)).then((ok) => {
      setSetting('pushEnabled', next && ok);
      paint();
    });
  };
  // The form is Google's and it is modal on its own: the dialog stays open behind it, so the player
  // lands back where they were. Repainted afterwards — a withdrawn consent can retire the row, and
  // leaving a dead entry point behind would be the one thing this row exists not to be.
  $('prefAds').onclick = () => {
    buzz('light');
    void openPrivacyOptions().then(paint);
  };
  $('prefWipe').onclick = () => void wipe();
  $('prefUsernameOpen').onclick = () => void openUsernameDialog();
  $('prefUsername').oninput = () => {
    $('prefUsernameErr').hidden = true;
    updateUsernameSaveState();
  };
  // La bannière AdMob est une vue native posée PAR-DESSUS la WebView (cf. src/ads.ts) : elle ne
  // sait rien du clavier virtuel, et se retrouve repositionnée juste là où le champ remonte pour
  // rester visible — bloquant la saisie sous elle. On la cache pendant que ce champ a le focus,
  // et on la réaffiche au blur.
  $('prefUsername').onfocus = () => void hideBannerForInput();
  $('prefUsername').onblur = () => void resumeBannerAfterInput();
  $('prefUsernameSave').onclick = () => void saveUsername();
  // Le dé remplit le champ, il ne valide pas : on relance autant qu'on veut, on retouche, et
  // « Valider » reste le seul geste qui engage. Le champ ne prend pas le focus — le clavier
  // virtuel monterait pour un nom que personne n'a encore envie de taper.
  $('prefUsernameDice').onclick = () => {
    // Pas pendant un enregistrement : le dé réactivait « Valider » (updateUsernameSaveState), et un
    // second PATCH partait avec un autre nom, l'erreur du premier s'affichant sous le second.
    if ($<HTMLInputElement>('prefUsername').disabled) return;
    $<HTMLInputElement>('prefUsername').value = randomUsername(currentLang());
    $('prefUsernameErr').hidden = true;
    updateUsernameSaveState();
    buzz('light');
    if (!noMotion()) {
      // Le dé seul tourne, plus tout le bouton : depuis qu'il porte son texte, le faire pivoter
      // entier renverserait la phrase.
      $('prefUsernameDice')
        .querySelector('svg')!
        .animate(
          [
            { transform: 'rotate(0)' },
            { transform: 'rotate(-200deg) scale(1.1)' },
            { transform: 'rotate(-180deg)' },
          ],
          { duration: 380, easing: 'cubic-bezier(.2,.9,.3,1.2)' },
        );
    }
  };
  $('prefRecoveryOpen').onclick = () => void openRecoveryDialog();
  $('prefRecoveryCopy').onclick = () => void copyRecoveryCode();
  $('prefRecoveryUse').onclick = () => void useRecoveryCode();
  $('prefRecoveryInput').oninput = () => {
    $<HTMLButtonElement>('prefRecoveryUse').disabled = !validCode(
      $<HTMLInputElement>('prefRecoveryInput').value,
    );
  };
  $('prefRecoveryCancel').onclick = () => closeRecoveryDialog();
  $('recoveryDialogClose').onclick = () => closeRecoveryDialog();
  $('recoveryDialog').onclick = (e) => {
    if (e.target === $('recoveryDialog')) closeRecoveryDialog();
  };
  $('prefUsernameCancel').onclick = () => closeUsernameDialog();
  $('usernameDialogClose').onclick = () => closeUsernameDialog();
  // Le clic sur le fond ferme, le clic sur la feuille elle-même ne ferme rien : le test porte
  // sur la cible, puisque le clic remonte de tout ce qu'elle contient.
  $('usernameDialog').onclick = (e) => {
    if (e.target === $('usernameDialog')) closeUsernameDialog();
  };
  wireSettings(paint);
}
